import React, { useState, useEffect, useRef, useCallback } from 'react';
import { get, post } from 'aws-amplify/api';
import { useAuth } from '../contexts/AuthContext';
import { formatDateTime } from '../utils/dateUtils';
import SubscriberManagement from '../components/job-applications/SubscriberManagement';
import './JobApplications.css';

// Position type colors
const POSITION_TYPE_COLORS = {
  FOH: '#3b82f6',
  BOH: '#22c55e',
  BOTH: '#8b5cf6',
};

// Status colors
const STATUS_COLORS = {
  PENDING: { bg: '#fef3c7', color: '#92400e' },
  PENDING_DUAL: { bg: '#e0e7ff', color: '#4338ca' },
  APPROVED: { bg: '#dcfce7', color: '#15803d' },
  DENIED: { bg: '#fee2e2', color: '#dc2626' },
};

// Display labels for statuses
const STATUS_LABELS = {
  PENDING: 'Pending',
  PENDING_DUAL: 'Awaiting 2nd Decision',
  APPROVED: 'Approved',
  DENIED: 'Denied',
};

// Availability labels
const AVAILABILITY_LABELS = {
  weekday_am: 'Weekday AM',
  weekday_pm: 'Weekday PM',
  weekend_am: 'Weekend AM',
  weekend_pm: 'Weekend PM',
};

const JobApplications = () => {
  const [applicationsByStatus, setApplicationsByStatus] = useState({ PENDING: [], PENDING_DUAL: [], APPROVED: [], DENIED: [] });
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showDualModal, setShowDualModal] = useState(false);
  const [dualModalApp, setDualModalApp] = useState(null);
  const [dualFohChoice, setDualFohChoice] = useState(null);
  const [dualBohChoice, setDualBohChoice] = useState(null);
  const { getAuthToken } = useAuth();

  const applications = applicationsByStatus[statusFilter] || [];
  const allApplications = [
    ...applicationsByStatus.PENDING,
    ...applicationsByStatus.PENDING_DUAL,
    ...applicationsByStatus.APPROVED,
    ...applicationsByStatus.DENIED
  ];

  const tabsRef = useRef(null);
  const tabsWrapperRef = useRef(null);

  const updateScrollIndicators = useCallback(() => {
    const tabs = tabsRef.current;
    const wrapper = tabsWrapperRef.current;
    if (!tabs || !wrapper) return;

    const { scrollLeft, scrollWidth, clientWidth } = tabs;
    if (scrollLeft > 0) {
      wrapper.classList.add('can-scroll-left');
    } else {
      wrapper.classList.remove('can-scroll-left');
    }
    if (scrollLeft + clientWidth < scrollWidth - 1) {
      wrapper.classList.add('can-scroll-right');
    } else {
      wrapper.classList.remove('can-scroll-right');
    }
  }, []);

  useEffect(() => {
    updateScrollIndicators();
    window.addEventListener('resize', updateScrollIndicators);
    return () => window.removeEventListener('resize', updateScrollIndicators);
  }, [updateScrollIndicators]);

  const fetchAllStatuses = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = await getAuthToken();
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      const statuses = ['PENDING', 'PENDING_DUAL', 'APPROVED', 'DENIED'];
      const results = await Promise.allSettled(
        statuses.map(status => {
          const restOperation = get({
            apiName: 'ChulasJobsAPI',
            path: '/v1/admin/jobs/applications',
            options: {
              headers: { Authorization: token },
              queryParams: { status: status },
            },
          });
          return restOperation.response.then(res => res.body.json());
        })
      );

      const newData = {};
      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          newData[statuses[index]] = result.value.applications || [];
        } else {
          console.error(`Failed to fetch ${statuses[index]}:`, result.reason);
          newData[statuses[index]] = [];
        }
      });

      setApplicationsByStatus(newData);
    } catch (err) {
      console.error('Error fetching applications:', err);
      setError('Failed to fetch applications');
    } finally {
      setLoading(false);
    }
  }, [getAuthToken]);

  // Fetch all statuses on component mount
  useEffect(() => {
    fetchAllStatuses();
  }, [fetchAllStatuses]);

  const fetchApplicationDetails = async (applicationId) => {
    try {
      const token = await getAuthToken();
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      const restOperation = get({
        apiName: 'ChulasJobsAPI',
        path: `/v1/admin/jobs/applications/${applicationId}`,
        options: {
          headers: {
            Authorization: token,
          },
        },
      });

      const response = await restOperation.response;
      const data = await response.body.json();

      setSelectedApplication(data.application);
      setShowModal(true);
    } catch (err) {
      console.error('Error fetching application details:', err);
      setError('Failed to fetch application details');
    }
  };

  const openDualDecisionModal = (app) => {
    setDualModalApp(app);
    setDualFohChoice(app.foh_decision || null);
    setDualBohChoice(app.boh_decision || null);
    setShowDualModal(true);
  };

  const closeDualModal = () => {
    setShowDualModal(false);
    setDualModalApp(null);
    setDualFohChoice(null);
    setDualBohChoice(null);
  };

  const submitDualDecision = async () => {
    if (!dualModalApp) return;

    const appId = dualModalApp.application_id;
    const alreadyHasFoh = !!dualModalApp.foh_decision;
    const alreadyHasBoh = !!dualModalApp.boh_decision;

    // Determine which decisions to send
    const decisions = [];
    if (!alreadyHasFoh && dualFohChoice) {
      decisions.push({ type: 'foh', action: dualFohChoice });
    }
    if (!alreadyHasBoh && dualBohChoice) {
      decisions.push({ type: 'boh', action: dualBohChoice });
    }

    if (decisions.length === 0) {
      setError('Please make a decision for each side.');
      return;
    }

    try {
      setActionLoading(true);
      setError(null);

      const token = await getAuthToken();
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      let lastResult = null;
      for (const decision of decisions) {
        const endpoint = decision.action === 'approve' ? 'approve' : 'deny';
        const restOperation = post({
          apiName: 'ChulasJobsAPI',
          path: `/v1/admin/jobs/applications/${appId}/${endpoint}`,
          options: {
            headers: {
              Authorization: token,
              'Content-Type': 'application/json',
            },
            body: { decision_type: decision.type },
          },
        });

        const response = await restOperation.response;
        lastResult = await response.body.json();
      }

      if (lastResult?.decision_complete) {
        if (lastResult.final_status === 'APPROVED' || lastResult.winning_side) {
          setSuccessMessage(`Application approved for ${lastResult.winning_side}! Approval email sent.${lastResult.email_sent ? '' : ' (Email delivery may have failed)'}`);
        } else {
          setSuccessMessage(`Application denied (both sides).${lastResult.email_sent ? ' Rejection email sent.' : ''}`);
        }
      } else {
        setSuccessMessage('Decision recorded. Awaiting second decision.');
      }

      closeDualModal();
      setShowModal(false);
      await fetchAllStatuses();
    } catch (err) {
      console.error('Error submitting dual decision:', err);
      setError(err.message || 'Failed to submit decision');
    } finally {
      setActionLoading(false);
    }
  };

  const approveApplication = async (applicationId, app) => {
    // For BOTH applicants, open dual decision modal
    if (app && app.position_type === 'BOTH') {
      openDualDecisionModal(app);
      return;
    }

    if (!window.confirm('Are you sure you want to approve this application? This will send an approval email to the applicant.')) {
      return;
    }

    try {
      setActionLoading(true);
      setError(null);

      const token = await getAuthToken();
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      const restOperation = post({
        apiName: 'ChulasJobsAPI',
        path: `/v1/admin/jobs/applications/${applicationId}/approve`,
        options: {
          headers: {
            Authorization: token,
          },
        },
      });

      const response = await restOperation.response;
      const data = await response.body.json();

      setSuccessMessage(`Application approved! Approval email sent to applicant.${data.email_sent ? '' : ' (Email delivery may have failed)'}`);
      setShowModal(false);
      await fetchAllStatuses();
    } catch (err) {
      console.error('Error approving application:', err);
      setError(err.message || 'Failed to approve application');
    } finally {
      setActionLoading(false);
    }
  };

  const denyApplication = async (applicationId, app) => {
    // For BOTH applicants, open dual decision modal
    if (app && app.position_type === 'BOTH') {
      openDualDecisionModal(app);
      return;
    }

    if (!window.confirm('Are you sure you want to deny this application? This will send a rejection email to the applicant.')) {
      return;
    }

    try {
      setActionLoading(true);
      setError(null);

      const token = await getAuthToken();
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      const restOperation = post({
        apiName: 'ChulasJobsAPI',
        path: `/v1/admin/jobs/applications/${applicationId}/deny`,
        options: {
          headers: {
            Authorization: token,
          },
        },
      });

      await restOperation.response;

      setSuccessMessage('Application denied. Rejection email sent to applicant.');
      setShowModal(false);
      await fetchAllStatuses();
    } catch (err) {
      console.error('Error denying application:', err);
      setError(err.message || 'Failed to deny application');
    } finally {
      setActionLoading(false);
    }
  };

  const viewResume = async (applicationId) => {
    try {
      const token = await getAuthToken();
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      const restOperation = get({
        apiName: 'ChulasJobsAPI',
        path: `/v1/admin/jobs/applications/${applicationId}/resume`,
        options: {
          headers: {
            Authorization: token,
          },
        },
      });

      const response = await restOperation.response;
      const data = await response.body.json();

      // Download the resume using the presigned URL
      const link = document.createElement('a');
      link.href = data.url;
      link.download = data.filename || 'resume.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error getting resume URL:', err);
      setError('Failed to get resume. The applicant may not have uploaded one.');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedApplication(null);
  };

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Calculate stats
  const stats = {
    total: allApplications.length,
    pending: allApplications.filter(a => a.status === 'PENDING' || a.status === 'PENDING_DUAL').length,
    approved: allApplications.filter(a => a.status === 'APPROVED').length,
    denied: allApplications.filter(a => a.status === 'DENIED').length,
  };

  return (
    <div className="job-applications-container">
      <div className="page-header">
        <h1>Job Applications</h1>
        <p className="page-description">
          Review and manage job applications.
        </p>
      </div>

      {/* Alerts */}
      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success">
          {successMessage}
        </div>
      )}

      {/* Stats Row */}
      <div className="stats-row">
        <div className="stat-card">
          <span className="stat-value">{stats.total}</span>
          <span className="stat-label">Total</span>
        </div>
        <div className="stat-card stat-pending">
          <span className="stat-value">{stats.pending}</span>
          <span className="stat-label">Pending</span>
        </div>
        <div className="stat-card stat-approved">
          <span className="stat-value">{stats.approved}</span>
          <span className="stat-label">Approved</span>
        </div>
        <div className="stat-card stat-denied">
          <span className="stat-value">{stats.denied}</span>
          <span className="stat-label">Denied</span>
        </div>
      </div>

      <SubscriberManagement />

      {/* Toolbar */}
      <div className="list-toolbar">
        <button onClick={fetchAllStatuses} className="btn btn-secondary" disabled={loading}>
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {/* Status Tabs */}
      <div className="status-tabs-wrapper" ref={tabsWrapperRef}>
        <div className="status-tabs" role="tablist" ref={tabsRef} onScroll={updateScrollIndicators}>
          {Object.entries(STATUS_LABELS).map(([key, label]) => (
            <button
              key={key}
              role="tab"
              aria-selected={statusFilter === key}
              className={`status-tab ${statusFilter === key ? 'status-tab-active' : ''}`}
              onClick={() => setStatusFilter(key)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Applications Table */}
      <div className="applications-table-wrapper">
        {loading ? (
          <div className="loading-cell">Loading applications...</div>
        ) : applications.length === 0 ? (
          <div className="empty-cell">
            <p>No applications found</p>
            <small>No {STATUS_LABELS[statusFilter].toLowerCase()} applications</small>
          </div>
        ) : (
          <table className="applications-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Position</th>
                <th>Status</th>
                <th>Applied</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {applications.map((app) => (
                <tr key={app.application_id}>
                  <td className="name-cell">
                    <div className="applicant-name">{app.full_name}</div>
                    <div className="applicant-email">{app.email}</div>
                  </td>
                  <td>
                    <div className="position-cell">
                      <span className="position-name">{app.position}</span>
                      <span
                        className="position-type-badge"
                        style={{ backgroundColor: POSITION_TYPE_COLORS[app.position_type] }}
                      >
                        {app.position_type}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div className="status-cell">
                      <span
                        className="status-badge"
                        style={{
                          backgroundColor: STATUS_COLORS[app.status]?.bg,
                          color: STATUS_COLORS[app.status]?.color,
                        }}
                      >
                        {STATUS_LABELS[app.status] || app.status}
                      </span>

                    </div>
                  </td>
                  <td className="date-cell">
                    {formatDateTime(app.created_at)}
                  </td>
                  <td className="actions-cell">
                    <button
                      onClick={() => fetchApplicationDetails(app.application_id)}
                      className="action-btn view-btn"
                    >
                      View
                    </button>
                    {(app.status === 'PENDING' || app.status === 'PENDING_DUAL') && (
                      <>
                        {app.position_type === 'BOTH' ? (
                          <button
                            onClick={() => openDualDecisionModal(app)}
                            className="action-btn approve-btn"
                            disabled={actionLoading}
                          >
                            {app.status === 'PENDING_DUAL' ? 'Make 2nd Decision' : 'Decide FOH/BOH'}
                          </button>
                        ) : (
                          <>
                            <button
                              onClick={() => approveApplication(app.application_id, app)}
                              className="action-btn approve-btn"
                              disabled={actionLoading}
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => denyApplication(app.application_id, app)}
                              className="action-btn deny-btn"
                              disabled={actionLoading}
                            >
                              Deny
                            </button>
                          </>
                        )}
                      </>
                    )}
                    {app.has_resume && (
                      <button
                        onClick={() => viewResume(app.application_id)}
                        className="action-btn resume-btn"
                      >
                        Resume
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Application Detail Modal */}
      {showModal && selectedApplication && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Application Details</h2>
              <button className="modal-close" onClick={closeModal}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="detail-section">
                <h3>Personal Information</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Name</span>
                    <span className="detail-value">{selectedApplication.full_name}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Email</span>
                    <span className="detail-value">{selectedApplication.email}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Phone</span>
                    <span className="detail-value">{selectedApplication.phone}</span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h3>Position</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Position</span>
                    <span className="detail-value">{selectedApplication.position}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Type</span>
                    <span
                      className="position-type-badge"
                      style={{ backgroundColor: POSITION_TYPE_COLORS[selectedApplication.position_type] }}
                    >
                      {selectedApplication.position_type}
                    </span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h3>Availability</h3>
                <div className="availability-tags">
                  {selectedApplication.availability?.map((avail) => (
                    <span key={avail} className="availability-tag">
                      {AVAILABILITY_LABELS[avail] || avail}
                    </span>
                  ))}
                </div>
                {selectedApplication.preferred_start_date && (
                  <div className="detail-item" style={{ marginTop: '12px' }}>
                    <span className="detail-label">Preferred Start Date</span>
                    <span className="detail-value">{selectedApplication.preferred_start_date}</span>
                  </div>
                )}
              </div>

              <div className="detail-section">
                <h3>Experience</h3>
                <div className="detail-grid">
                  {selectedApplication.years_experience && (
                    <div className="detail-item">
                      <span className="detail-label">Years Experience</span>
                      <span className="detail-value">{selectedApplication.years_experience}</span>
                    </div>
                  )}
                </div>
                {selectedApplication.previous_experience && (
                  <div className="detail-item full-width">
                    <span className="detail-label">Previous Experience</span>
                    <p className="detail-text">{selectedApplication.previous_experience}</p>
                  </div>
                )}
              </div>

              {selectedApplication.additional_comments && (
                <div className="detail-section">
                  <h3>Additional Comments</h3>
                  <p className="detail-text">{selectedApplication.additional_comments}</p>
                </div>
              )}

              {selectedApplication.referral_source && (
                <div className="detail-section">
                  <h3>Referral Source</h3>
                  <span className="detail-value">{selectedApplication.referral_source}</span>
                </div>
              )}

              <div className="detail-section">
                <h3>Status</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Current Status</span>
                    <span
                      className="status-badge"
                      style={{
                        backgroundColor: STATUS_COLORS[selectedApplication.status]?.bg,
                        color: STATUS_COLORS[selectedApplication.status]?.color,
                      }}
                    >
                      {STATUS_LABELS[selectedApplication.status] || selectedApplication.status}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Applied</span>
                    <span className="detail-value">{formatDateTime(selectedApplication.created_at)}</span>
                  </div>
                </div>
                {(selectedApplication.foh_decision || selectedApplication.boh_decision) && (
                  <div className="detail-grid" style={{ marginTop: '12px' }}>
                    {selectedApplication.foh_decision && (
                      <div className="detail-item">
                        <span className="detail-label">FOH Decision</span>
                        <span className={`dual-decision-badge ${selectedApplication.foh_decision}`}>
                          {selectedApplication.foh_decision === 'approve' ? 'Approved' : 'Denied'}
                        </span>
                      </div>
                    )}
                    {selectedApplication.boh_decision && (
                      <div className="detail-item">
                        <span className="detail-label">BOH Decision</span>
                        <span className={`dual-decision-badge ${selectedApplication.boh_decision}`}>
                          {selectedApplication.boh_decision === 'approve' ? 'Approved' : 'Denied'}
                        </span>
                      </div>
                    )}
                  </div>
                )}
                {selectedApplication.scheduling_link && (
                  <div className="detail-item full-width" style={{ marginTop: '12px' }}>
                    <span className="detail-label">Scheduling Link</span>
                    <a
                      href={selectedApplication.scheduling_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="scheduling-link"
                    >
                      {selectedApplication.scheduling_link}
                    </a>
                  </div>
                )}

              </div>
            </div>

            <div className="modal-actions">
              {selectedApplication.has_resume && (
                <button
                  onClick={() => viewResume(selectedApplication.application_id)}
                  className="btn btn-secondary"
                >
                  View Resume
                </button>
              )}
              {(selectedApplication.status === 'PENDING' || selectedApplication.status === 'PENDING_DUAL') && (
                <>
                  {selectedApplication.position_type === 'BOTH' ? (
                    <button
                      onClick={() => openDualDecisionModal(selectedApplication)}
                      className="btn btn-primary"
                      disabled={actionLoading}
                    >
                      {selectedApplication.status === 'PENDING_DUAL' ? 'Make 2nd Decision' : 'Decide FOH/BOH'}
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={() => denyApplication(selectedApplication.application_id, selectedApplication)}
                        className="btn btn-danger"
                        disabled={actionLoading}
                      >
                        {actionLoading ? 'Processing...' : 'Deny'}
                      </button>
                      <button
                        onClick={() => approveApplication(selectedApplication.application_id, selectedApplication)}
                        className="btn btn-primary"
                        disabled={actionLoading}
                      >
                        {actionLoading ? 'Processing...' : 'Approve'}
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Dual Decision Modal for BOTH applicants */}
      {showDualModal && dualModalApp && (
        <div className="modal-overlay" onClick={closeDualModal}>
          <div className="modal-content dual-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>FOH / BOH Decision</h2>
              <button className="modal-close" onClick={closeDualModal}>&times;</button>
            </div>
            <div className="modal-body">
              <p className="dual-modal-subtitle">
                <strong>{dualModalApp.full_name}</strong> applied for both FOH and BOH.
                {dualModalApp.status === 'PENDING_DUAL' && ' One decision has already been made.'}
              </p>

              {/* FOH Decision */}
              <div className={`dual-decision-section ${dualModalApp.foh_decision ? 'decided' : ''}`}>
                <h3>FOH Decision</h3>
                {dualModalApp.foh_decision ? (
                  <div className="dual-decision-locked">
                    <span className={`dual-decision-badge ${dualModalApp.foh_decision}`}>
                      {dualModalApp.foh_decision === 'approve' ? 'Approved' : 'Denied'}
                    </span>
                    <span className="decided-label">(already decided)</span>
                  </div>
                ) : (
                  <div className="dual-decision-buttons">
                    <button
                      className={`dual-btn dual-approve ${dualFohChoice === 'approve' ? 'selected' : ''}`}
                      onClick={() => setDualFohChoice('approve')}
                    >
                      Approve FOH
                    </button>
                    <button
                      className={`dual-btn dual-deny ${dualFohChoice === 'deny' ? 'selected' : ''}`}
                      onClick={() => setDualFohChoice('deny')}
                    >
                      Deny FOH
                    </button>
                  </div>
                )}
              </div>

              {/* BOH Decision */}
              <div className={`dual-decision-section ${dualModalApp.boh_decision ? 'decided' : ''}`}>
                <h3>BOH Decision</h3>
                {dualModalApp.boh_decision ? (
                  <div className="dual-decision-locked">
                    <span className={`dual-decision-badge ${dualModalApp.boh_decision}`}>
                      {dualModalApp.boh_decision === 'approve' ? 'Approved' : 'Denied'}
                    </span>
                    <span className="decided-label">(already decided)</span>
                  </div>
                ) : (
                  <div className="dual-decision-buttons">
                    <button
                      className={`dual-btn dual-approve ${dualBohChoice === 'approve' ? 'selected' : ''}`}
                      onClick={() => setDualBohChoice('approve')}
                    >
                      Approve BOH
                    </button>
                    <button
                      className={`dual-btn dual-deny ${dualBohChoice === 'deny' ? 'selected' : ''}`}
                      onClick={() => setDualBohChoice('deny')}
                    >
                      Deny BOH
                    </button>
                  </div>
                )}
              </div>

              <div className="dual-decision-info">
                <p><strong>Rules:</strong></p>
                <ul>
                  <li>Both approve → FOH wins</li>
                  <li>Only FOH approves → FOH</li>
                  <li>Only BOH approves → BOH</li>
                  <li>Both deny → Denial email sent</li>
                </ul>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={closeDualModal}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={submitDualDecision}
                disabled={actionLoading || (
                  !dualModalApp.foh_decision && !dualFohChoice
                ) || (
                  !dualModalApp.boh_decision && !dualBohChoice
                )}
              >
                {actionLoading ? 'Processing...' : 'Submit Decision'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobApplications;
